import { ReactElement, useCallback } from "react";
import styled from "@emotion/styled/macro";
import { useNavigate } from "react-router-dom";
import { NavigationItem } from "adapters/navigation";
import { getNewPath } from "adapters/common";
import sunrunLogo from "assets/images/Logo.svg";

type MobileMenuOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  remainingNavItems: NavigationItem[];
  secondaryNavItems: NavigationItem[];
  getIconForNavItem: (item: NavigationItem) => ReactElement;
  onItemClick?: (item: NavigationItem) => void;
  extraItems?: ReactElement | null;
};

const MobileMenuOverlay = ({
  isOpen,
  onClose,
  remainingNavItems,
  secondaryNavItems,
  getIconForNavItem,
  onItemClick,
  extraItems,
}: MobileMenuOverlayProps) => {
  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      path: string,
      item: NavigationItem
    ) => {
      e.preventDefault();

      // If onItemClick is provided, use it instead of default navigation
      if (onItemClick) {
        onItemClick(item);
        return;
      }

      navigate(getNewPath(path));
      // Don't close immediately to prevent re-renders
      setTimeout(() => {
        onClose();
      }, 0);
    },
    [navigate, onClose, onItemClick]
  );

  if (!isOpen) return null;

  return (
    <Overlay>
      <MenuContainer>
        <Header>
          <LogoImage src={sunrunLogo} alt="Sunrun" />
        </Header>

        <MenuContent>
          <MenuSection>
            {remainingNavItems.map((item) => (
              <MenuItem
                key={`${item.id}-mobile`}
                href={getNewPath(item.path)}
                onClick={(e) => handleItemClick(e, item.path, item)}
                data-testid={`mobile-menu-item-${item.id}`}
              >
                {getIconForNavItem(item)}
                <MenuItemText
                  itemId={item.id}
                  data-testid={`mobile-menu-item-text-${item.id}`}
                >
                  {item.label}
                </MenuItemText>
              </MenuItem>
            ))}
          </MenuSection>

          <Separator />

          <MenuSection>
            {secondaryNavItems.map((item) => (
              <MenuItem
                key={`${item.id}-mobile`}
                href={getNewPath(item.path)}
                onClick={(e) => handleItemClick(e, item.path, item)}
                data-testid={`mobile-menu-item-${item.id}`}
              >
                {getIconForNavItem(item)}
                <MenuItemText
                  itemId={item.id}
                  data-testid={`mobile-menu-item-text-${item.id}`}
                >
                  {item.label}
                </MenuItemText>
              </MenuItem>
            ))}
          </MenuSection>
          {extraItems}
        </MenuContent>
      </MenuContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #1f2647;
  z-index: 99;
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding-bottom: 40px;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0px 16px;
  margin-top: 32px;
`;

const LogoImage = styled.img`
  height: 32px;
  width: auto;
`;

const MenuContent = styled.div`
  flex: 1;
  padding: 32px 0;
  overflow-y: auto;
`;

const MenuSection = styled.div`
  padding: 0 24px;
`;

const MenuItem = styled.a`
  display: flex;
  align-items: center;
  padding: 16px 0;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;

  &:hover {
    background-color: #22263a;
    padding-left: 16px;
    padding-right: 16px;
  }

  svg {
    margin-right: 16px;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ itemId: string }>`
  font-size: 16px;
  font-weight: 400;
  text-transform: capitalize;
  color: white;
  margin-left: ${(props) =>
    props.itemId === "notifications" || props.itemId === "messages"
      ? "16px"
      : "0"};
`;

const Separator = styled.div`
  height: 1px;
  background-color: #49517d;
  margin: 24px 24px;
`;

export { MobileMenuOverlay };
